/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * (Focal theme docs trimmed for brevity — unchanged from your file.)
 */



// =======================================================
// HOBIEGO — SPLIT SHIPPING (Option A) — UK ONLY
// Fee variant: 56318235509120
// Goal: NO cart:refresh (prevents flicker + stops "You may also like" refreshing)
// We manually:
//  - add/remove fee
//  - re-fetch cart.js
//  - sync split UI
//  - update totals (main cart + mini cart checkout total)
//
// Pill-flicker fix: while a toggle is in flight (toggleActive), the background
// observers (mini-cart open, cart:updated cleanup) MUST NOT run syncUI —
// otherwise they re-read a mid-flight cart and flip the radio back to the other
// pill before the real result lands. The change handler owns the radio state
// during the toggle.
//
// Total-storm fix: hookMainCartRerender is now a NO-OP. It used to install a
// #MainContent MutationObserver that re-fetched + repainted on any DOM change;
// on desktop the sticky summary re-pin + our own total writes kept poking it,
// causing a repaint loop (the flicker). The total is already kept in sync by
// the change handler, the cart:updated cleanup, DOMContentLoaded, and pageshow.
// =======================================================

(function () {
  const SPLIT_FEE_VARIANT_ID = 56318235509120;
  const UK_ISO = 'GB';

  // True from the click until the toggle fully settles. While set, background
  // re-syncs bail so they can't flip the pills mid-request.
  let toggleActive = false;

  // ---------- helpers ----------

  function isPreorderTitle(title = '') {
    const t = String(title || '')
      .toUpperCase()
      .replace(/\s+/g, ' ')
      .trim();

    return (
      t.startsWith('PRE-ORDER') ||
      t.startsWith('PREORDER') ||
      t.startsWith('PRE ORDER') ||
      /^[^A-Z0-9]*PRE[- ]?ORDER\b/.test(t)
    );
  }

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  function feeItem(cart) {
    return (cart.items || []).find((i) => Number(i.id) === Number(SPLIT_FEE_VARIANT_ID)) || null;
  }

  function hasFee(cart) {
    return !!feeItem(cart);
  }

  function cartIsMixed(cart) {
    let hasPre = false;
    let hasNonPre = false;

    (cart.items || []).forEach((item) => {
      if (Number(item.id) === Number(SPLIT_FEE_VARIANT_ID)) return;

      const title = (item.product_title || item.title || '');
      if (isPreorderTitle(title)) hasPre = true;
      else hasNonPre = true;
    });

    return hasPre && hasNonPre;
  }

  async function addFee() {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: SPLIT_FEE_VARIANT_ID, quantity: 1 })
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      console.error('[HOBIEGO Split Shipping] addFee failed:', res.status, txt);
      throw new Error('Failed to add split fee');
    }
  }

  async function removeFee(cart) {
    const fee = feeItem(cart);
    if (!fee) return;

    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: fee.key, quantity: 0 })
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      console.error('[HOBIEGO Split Shipping] removeFee failed:', res.status, txt);
      throw new Error('Failed to remove split fee');
    }
  }

  function setRadiosDisabled(disabled) {
    document
      .querySelectorAll('[data-hobiego-split-wrap] input[name="hobiego_shipping_choice"]')
      .forEach((r) => (r.disabled = disabled));
  }

  function setWrapBusy(isBusy) {
    document.querySelectorAll('[data-hobiego-split-wrap]').forEach((wrap) => {
      if (isBusy) {
        wrap.dataset.hobiegoBusy = '1';
        wrap.hidden = false;
        wrap.style.pointerEvents = 'none'; // block clicks mid-request — no visible change
      } else {
        wrap.dataset.hobiegoBusy = '';
        wrap.style.pointerEvents = '';
      }
    });
  }

  function isUKWrapFromEventTarget(target) {
    const wrap = target && target.closest ? target.closest('[data-hobiego-split-wrap]') : null;
    if (!wrap) return false;
    return String(wrap.dataset.hobiegoCountry || '').toUpperCase() === UK_ISO;
  }

  function syncUI(cart) {
    const feePresent = hasFee(cart);
    const mixed = cartIsMixed(cart);

    document.querySelectorAll('[data-hobiego-split-wrap]').forEach((wrap) => {
      const country = String(wrap.dataset.hobiegoCountry || '').toUpperCase();

      if (country !== UK_ISO) {
        wrap.hidden = true;
        return;
      }

      const shouldShow = mixed || feePresent;

      // never hide while busy
      const busy = wrap.dataset.hobiegoBusy === '1';
      wrap.hidden = busy ? false : !shouldShow;

      const together = wrap.querySelector('[data-hobiego-ship-together]');
      const split = wrap.querySelector('[data-hobiego-split-ship]');

      if (together && split) {
        split.checked = feePresent;
        together.checked = !feePresent;
      }
    });
  }

  function formatMoney(cents, currency) {
    const cur = (currency || 'GBP').toUpperCase();
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: 2
      }).format((cents || 0) / 100);
    } catch (e) {
      return '£' + ((cents || 0) / 100).toFixed(2);
    }
  }

  function updateCartPageTotal(cart) {
    const totalContainer = document.querySelector('.cart__total-container');
    if (!totalContainer) return;

    const spans = totalContainer.querySelectorAll('span');
    if (spans.length < 2) return;

    const money = formatMoney(cart.total_price, cart.currency);
    const code = (cart.currency || '').toUpperCase();
    const text = code ? `${money} ${code}` : money;
    if (spans[1].textContent !== text) spans[1].textContent = text;
  }

  function updateMiniCartCheckoutTotal(cart) {
    const btn = document.querySelector('#mini-cart .checkout-button');
    if (!btn) return;

    const sep = btn.querySelector('.square-separator');
    if (!sep) return;

    const money = formatMoney(cart.total_price, cart.currency);
    const code = (cart.currency || '').toUpperCase();
    const text = code ? `${money} ${code}` : money;

    let node = sep.nextSibling;
    while (node && node.nodeType !== Node.TEXT_NODE) node = node.nextSibling;

    if (node && node.nodeType === Node.TEXT_NODE) {
      node.textContent = ' ' + text;
    } else {
      let span = btn.querySelector('[data-hobiego-mini-total]');
      if (!span) {
        span = document.createElement('span');
        span.setAttribute('data-hobiego-mini-total', '');
        btn.appendChild(span);
      }
      span.textContent = ' ' + text;
    }
  }

  async function refreshMiniCartTotalFromServer(retries = 6) {
    for (let i = 0; i < retries; i++) {
      try {
        const cart = await getCart();
        updateMiniCartCheckoutTotal(cart);
        return;
      } catch (e) {}
      await new Promise((r) => setTimeout(r, 120));
    }
  }

  function makeThrottledRunner(fn) {
    let running = false;
    let queued = false;

    return async function runThrottled() {
      if (running) { queued = true; return; }
      running = true;
      queued = false;

      try { await fn(); }
      finally {
        running = false;
        if (queued) runThrottled().catch(() => {});
      }
    };
  }

  function hookMiniCartOpen() {
    const mini = document.getElementById('mini-cart');
    if (!mini) return;

    const run = makeThrottledRunner(async () => {
      if (toggleActive) return; // don't fight an in-flight toggle
      const cart = await getCart();
      syncUI(cart);
      updateMiniCartCheckoutTotal(cart);
    });

    run().catch(() => {});

    const obsOpen = new MutationObserver(() => {
      if (mini.hasAttribute('open')) run().catch(() => {});
    });
    obsOpen.observe(mini, { attributes: true, attributeFilter: ['open'] });

    const obsDom = new MutationObserver(() => {
      if (mini.hasAttribute('open')) run().catch(() => {});
    });
    obsDom.observe(mini, { childList: true, subtree: true });
  }

  function hookMainCartRerender() {
    // DISABLED (no-op) — this used to install a #MainContent MutationObserver
    // that re-fetched the cart and repainted on ANY DOM change in the cart area.
    // On desktop the sticky summary's re-pin + our own total writes kept poking
    // it, causing a repaint loop (the total/pill flicker). It is redundant: the
    // total is already kept in sync by the change handler, the cart:updated
    // cleanup, DOMContentLoaded, and pageshow, and quantity changes on this cart
    // are full-page reloads. Left as a no-op intentionally — do not re-add the
    // observer or the flicker returns.
    return;
  }

  document.addEventListener('change', async (e) => {
    const el = e.target;
    if (!el || el.name !== 'hobiego_shipping_choice') return;

    try {
      if (!isUKWrapFromEventTarget(el)) return;

      toggleActive = true; // claim the radio state for the whole toggle
      setRadiosDisabled(true);
      setWrapBusy(true);

      let cart = await getCart();

      if (!cartIsMixed(cart)) {
        if (hasFee(cart)) await removeFee(cart);

        cart = await getCart();
        syncUI(cart);
        updateCartPageTotal(cart);
        updateMiniCartCheckoutTotal(cart);
        return;
      }

      if (el.value === 'split') {
        if (!hasFee(cart)) await addFee();
      } else {
        if (hasFee(cart)) await removeFee(cart);
      }

      cart = await getCart();
      syncUI(cart);
      updateCartPageTotal(cart);
      updateMiniCartCheckoutTotal(cart);

      await refreshMiniCartTotalFromServer();
    } catch (err) {
      console.error('[HOBIEGO Split Shipping] change handler error', err);
    } finally {
      setWrapBusy(false);
      setRadiosDisabled(false);
      toggleActive = false; // settled → observers may resume
    }
  });

  const hbAutoCleanupOnCartUpdate = makeThrottledRunner(async (cartFromEvent) => {
    if (toggleActive) return; // our toggle owns the state right now
    let cart = cartFromEvent || await getCart();

    if (!cartIsMixed(cart) && hasFee(cart)) {
      setWrapBusy(true);
      try {
        await removeFee(cart);
        cart = await getCart();
      } finally {
        setWrapBusy(false);
      }
    }

    syncUI(cart);
    updateCartPageTotal(cart);
    updateMiniCartCheckoutTotal(cart);
  });

  document.addEventListener('cart:updated', (event) => {
    hbAutoCleanupOnCartUpdate(event?.detail?.cart).catch(() => {});
  });

  // Load-time cleanup: removing the pre-order on the cart page is a full-page
  // reload, so the cart:updated cleanup above never fires for it. On load, if the
  // cart is no longer mixed but a stray split fee remains, drop it — that defaults
  // the price back to ship-together and (via syncUI) hides the split pills.
  // No setWrapBusy here, so the pills never flash visible while we tidy up.
  async function initSyncAndCleanup() {
    let cart = await getCart();

    if (!cartIsMixed(cart) && hasFee(cart)) {
      try {
        await removeFee(cart);
        cart = await getCart();
      } catch (e) {}
    }

    syncUI(cart);
    updateCartPageTotal(cart);
    updateMiniCartCheckoutTotal(cart);
  }

  document.addEventListener('DOMContentLoaded', async () => {
    try {
      await initSyncAndCleanup();
    } catch (err) {
      console.error('[HOBIEGO Split Shipping] init error', err);
    }

    hookMiniCartOpen();
    hookMainCartRerender();
  });

  // ✅ bfcache / instant.page safety re-sync (MUST be inside this IIFE)
  window.addEventListener('pageshow', () => {
    initSyncAndCleanup().catch(() => {});
  });

})();

// =======================================================
// HOBIEGO — Cart icon opens the MAIN CART PAGE, not the drawer
// Cart type stays "Message" (so add-to-cart still shows the toast), but any
// attempt to OPEN the mini-cart drawer — the header cart icon, the mobile cart
// icon, anything that controls #mini-cart — is redirected to /cart instead.
// Capture-phase + stopImmediatePropagation so we beat Focal's own toggle handler.
// =======================================================
(function () {
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
  var CART_URL = root + 'cart';

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    // Anything wired to open the mini-cart drawer controls #mini-cart.
    var opener = e.target.closest('[aria-controls="mini-cart"]');
    if (!opener) return;

    // Don't hijack the close button INSIDE the drawer (it also lives in #mini-cart).
    if (opener.closest('#mini-cart')) return;

    // IMPORTANT: the add-to-cart button (and quick-buy / product forms) ALSO point at
    // #mini-cart so the drawer can open after a successful add. Rather than exclude
    // every add-to-cart variant, we POSITIVELY require the opener to be the cart ICON
    // inside the site HEADER. The product form, sticky add bar and quick-buy all live
    // in the page body, so they're never touched — adding to cart shows the "Message"
    // toast as normal. Only the header / mobile cart icon redirects.
    if (!opener.closest('#shopify-section-header, .shopify-section-header, .header__wrapper, .header, .mobile-nav, .drawer-menu')) return;

    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    window.location.href = CART_URL;
  }, true);
})();

// =======================================================
// HOBIEGO — Header cart bubble count excludes Split Shipping fee
// Fixes flicker: theme briefly writes raw item_count (4) then we correct (3)
// This observer corrects the DOM immediately when it changes.
// Fee variant: 56318235509120
// =======================================================
(() => {
  const FEE_ID = 56318235509120;

  // Keep last known adjusted count so we can correct instantly
  let lastAdjusted = null;

  function adjustedCountFromCart(cart) {
    const raw = Number(cart?.item_count || 0);
    const feeQty = (cart?.items || []).reduce((sum, it) => {
      return sum + (Number(it?.id) === Number(FEE_ID) ? Number(it?.quantity || 0) : 0);
    }, 0);
    return Math.max(0, raw - feeQty);
  }

  function setAllBubbles(n) {
    document.querySelectorAll('cart-count.header__cart-count').forEach((el) => {
      const wanted = String(n);
      if (el.textContent !== wanted) el.textContent = wanted;
    });
  }

  async function fetchCartAndSet() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      lastAdjusted = adjustedCountFromCart(cart);
      setAllBubbles(lastAdjusted);
    } catch (e) {}
  }

  // 1) Apply ASAP (DOMContentLoaded)
  document.addEventListener('DOMContentLoaded', fetchCartAndSet);

  // 2) When Focal emits cart updates, update cache + set
  document.addEventListener('cart:updated', (event) => {
    const cart = event?.detail?.cart;
    if (cart) {
      lastAdjusted = adjustedCountFromCart(cart);
      setAllBubbles(lastAdjusted);
    } else {
      fetchCartAndSet();
    }
  });

  // 3) Instant anti-flicker: watch bubbles for any change and immediately correct
  function installObserver() {
    const obs = new MutationObserver(() => {
      if (lastAdjusted == null) return; // nothing cached yet
      setAllBubbles(lastAdjusted);
    });

    // Watch the whole document because header can be re-rendered / swapped
    obs.observe(document.documentElement, {
      subtree: true,
      childList: true,
      characterData: true
    });

    // Also do a quick pass in case header appeared after load
    if (lastAdjusted != null) setAllBubbles(lastAdjusted);
  }

  installObserver();

  // 4) bfcache / instant.page safety
  window.addEventListener('pageshow', fetchCartAndSet);
})();





/* HOBIEGO — Quick Buy overlay fix (prevents clipping by overflow parents like .scroller)
   Detach to <body> while open, then reattach when closed.
*/
(() => {
  const store = new WeakMap();

  // Support both element names just in case theme swaps them
  const isQuickBuyEl = (el) =>
    el && (el.tagName === 'QUICK-BUY-DRAWER' || el.tagName === 'QUICK-BUY-POPOVER');

  function detach(el) {
    if (!el || store.has(el)) return;
    const parent = el.parentNode;
    if (!parent) return;

    const marker = document.createComment('hobiego-qb-marker');
    parent.insertBefore(marker, el);

    store.set(el, { parent, marker });
    document.body.appendChild(el);
  }

  function reattach(el) {
    const rec = store.get(el);
    if (!rec) return;

    rec.parent.insertBefore(el, rec.marker);
    rec.marker.remove();
    store.delete(el);
  }

  function sync(el) {
    if (!isQuickBuyEl(el)) return;
    const isOpen = el.hasAttribute('open');
    if (isOpen) detach(el);
    else reattach(el);
  }

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.attributeName === 'open') {
        sync(m.target);
      } else if (m.type === 'childList') {
        m.addedNodes.forEach((n) => {
          if (isQuickBuyEl(n)) sync(n);
          if (n?.querySelectorAll) {
            n.querySelectorAll('quick-buy-drawer, quick-buy-popover').forEach(sync);
          }
        });
      }
    }
  });

  mo.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['open'],
  });

  // Initial pass
  document.querySelectorAll('quick-buy-drawer, quick-buy-popover').forEach(sync);
})();



/* HOBIEGO — Quick Buy: hide Shop Pay + More options until the wallet stops remounting (quiet window) */
(() => {
  const state = new WeakMap();

  function getTargets(drawer) {
    const wallet = drawer.querySelector('shop-pay-wallet-button');
    const more = drawer.querySelector('.shopify-payment-button__more-options');
    return { wallet, more };
  }

  function show(drawer) {
    drawer.classList.remove('hobiego-hide-wallet');
    const s = state.get(drawer);
    if (!s) return;

    if (s.mo) s.mo.disconnect();
    if (s.raf) cancelAnimationFrame(s.raf);
    if (s.t) clearTimeout(s.t);

    state.delete(drawer);
  }

  function hideAndWaitForQuiet(drawer) {
    if (!drawer) return;

    // reset any previous watcher for this drawer
    const prev = state.get(drawer);
    if (prev) {
      if (prev.mo) prev.mo.disconnect();
      if (prev.raf) cancelAnimationFrame(prev.raf);
      if (prev.t) clearTimeout(prev.t);
      state.delete(drawer);
    }

    drawer.classList.add('hobiego-hide-wallet');

    const quietMs = 260;        // how long it must be "quiet"
    const hardTimeoutMs = 2500; // safety net so it never stays hidden forever
    const startedAt = performance.now();

    const s = { mo: null, t: null, raf: null, lastMut: performance.now() };
    state.set(drawer, s);

    const bump = () => { s.lastMut = performance.now(); };

    // Observe changes inside the drawer, but focus on the payment area if possible
    const { wallet } = getTargets(drawer);
    const observeRoot = wallet || drawer;

    s.mo = new MutationObserver(() => bump());
    s.mo.observe(observeRoot, { subtree: true, childList: true, attributes: true });

    // Also bump on resize/layout changes (iframe often resizes)
    const onResize = () => bump();
    window.addEventListener('resize', onResize, { passive: true });

    const cleanupResize = () => window.removeEventListener('resize', onResize);

    const stableHeight = () => {
      const { wallet, more } = getTargets(drawer);
      const wH = wallet ? wallet.getBoundingClientRect().height : 0;
      const mVis = more ? more.offsetParent !== null : true; // if missing, don't block
      return (wH > 10) && mVis;
    };

    // Require stability across TWO animation frames (prevents "one good frame" then swap)
    let stableFrames = 0;

    const tick = () => {
      const now = performance.now();

      // Hard timeout
      if ((now - startedAt) > hardTimeoutMs) {
        cleanupResize();
        show(drawer);
        return;
      }

      // Count stable frames only if:
      // 1) payment area has height
      // 2) no mutations for quietMs
      if (stableHeight() && (now - s.lastMut) >= quietMs) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
      }

      if (stableFrames >= 2) {
        cleanupResize();
        show(drawer);
        return;
      }

      s.raf = requestAnimationFrame(tick);
    };

    s.raf = requestAnimationFrame(tick);
  }

  // Run when QUICK-BUY-DRAWER opens
  const moOpen = new MutationObserver((ms) => {
    for (const m of ms) {
      if (m.type === 'attributes' && m.attributeName === 'open' && m.target.tagName === 'QUICK-BUY-DRAWER') {
        const drawer = m.target;
        if (drawer.hasAttribute('open')) hideAndWaitForQuiet(drawer);
        else show(drawer);
      }
    }
  });

  moOpen.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
})();





// =======================================================
// HOBIEGO — Hide 'hidden-product' tagged items from predictive search
// DOM-based approach: removes hidden products after they render
// =======================================================
(() => {
  function removeHiddenProducts() {
    document.querySelectorAll('.predictive-search__product-item').forEach(item => {
      const link = item.querySelector('.product-item-meta__title');
      if (!link) return;
      // Check if the title matches the hidden product
      if (link.textContent.trim() === 'Split Shipping Fee') {
        item.remove();
      }
    });
  }

  const observer = new MutationObserver(removeHiddenProducts);
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true
  });
})();


/* HobiEgo — hold cart notification open for HOLD_MS, overriding Focal's own ~5s timer.
   Focal's theme.js startTimer() sets [hidden] early; we re-open the toast each time
   something hides it until our own clock elapses, then let it close for good.
   EXCEPTION: if the user clicks the X (or anywhere that dismisses it), we stop
   guarding immediately so the close actually sticks. */
(function () {
  var HOLD_MS = 10000;          // how long WE want the toast visible
  var holdUntil = 0;            // timestamp until which we keep it open
  var note = null;
  var guarding = false;         // true while we're actively holding this toast
  var releaseTimer = null;
  var userClosed = false;       // set true the moment the user dismisses it

  function getNote() {
    return document.querySelector('cart-notification.cart-notification');
  }

  function stopGuard() {
    guarding = false;
    clearTimeout(releaseTimer);
  }

  // When the toast becomes visible, start a fresh hold window.
  function beginHold(n) {
    note = n;
    holdUntil = performance.now() + HOLD_MS;
    guarding = true;
    userClosed = false;         // fresh toast → reset the manual-close flag

    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(function () {
      guarding = false;
      if (note) {
        var close = note.querySelector('.cart-notification__close');
        if (close) close.click(); else note.setAttribute('hidden', '');
      }
    }, HOLD_MS);
  }

  // If Focal (or anything) hides the toast before our window is up, re-open it —
  // UNLESS the user closed it on purpose.
  function reopenIfTooEarly() {
    if (userClosed) return;
    if (!guarding || !note) return;
    if (performance.now() < holdUntil && note.hasAttribute('hidden')) {
      note.removeAttribute('hidden');
    }
  }

  // Any click on the close button (or its icon) = user wants it gone. Stop the
  // hold so our observer doesn't fight the dismissal, then make sure it's hidden.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var closer = e.target.closest('.cart-notification__close, [data-action="close"]');
    if (!closer) return;
    if (!closer.closest('cart-notification')) return;

    userClosed = true;
    stopGuard();

    var n = getNote();
    if (n) n.setAttribute('hidden', '');
  }, true);

  var mo = new MutationObserver(function () {
    var n = getNote();
    if (!n) return;

    // toast just shown (no [hidden]) and we're not already holding it → begin a hold
    if (!n.hasAttribute('hidden') && (!guarding || note !== n)) {
      beginHold(n);
      return;
    }

    // toast was hidden too early → undo it (unless the user closed it)
    if (n === note) reopenIfTooEarly();
  });

  mo.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['hidden']
  });

  document.addEventListener('DOMContentLoaded', function () {
    var n = getNote();
    if (n && !n.hasAttribute('hidden')) beginHold(n);
  });
})();